import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { callApi, callStreamingApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import Loading from "src/components/Loading";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridItemChangeEvent,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { SortDescriptor, orderBy, CompositeFilterDescriptor, filterBy } from "@progress/kendo-data-query";
import { DatePicker, DatePickerChangeEvent, DateRangePicker, DateRangePickerChangeEvent } from "@progress/kendo-react-dateinputs";
import { Popup } from "@progress/kendo-react-popup";
import { ExcelExport, ExcelExportColumnProps } from "@progress/kendo-react-excel-export";
import styles from "./BrandedCallingBilling.module.css";

interface AcctIdMap {
  acctMapId: number;
  vendorId: number;
  vendorName: string;
  businessName: string;
  acctId: string;
  inEdit?: boolean;
  isNew?: boolean;
}

interface Vendor {
  vendorId: number;
  vendorName: string;
}

interface FoUsageRow {
  business: string;
  businessUnit: string;
  program: string;
  billingMonth: string;
  totalTreatedCalls: number | null;
  logoCalls: number | null;
  tmoTreatedCalls: number | null;
  attTreatedCalls: number | null;
  vzwTreatedCalls: number | null;
  boostTreatedCalls: number | null;
  rogersBellTreatedCalls: number | null;
  uniqueANumbers: number | null;
  answerRate: number | null;
  declineRate: number | null;
  talkRate10s: number | null;
  engagementRate60s: number | null;
  acctId: string | null;
}

interface TnsUsageRow {
  billingDate: string;
  enterpriseName: string;
  terminatingCarrier: string;
  brandingType: string;
  volume: number;
  acctId: string | null;
}

interface CombinedUsageRow {
  vendor: string;
  carrier: string;
  acctId: string | null;
  businessName: string;
  billingDate: string;
  usage: number;
}

interface InvoiceDataRow {
  accountNumId: number;
  acctId: string;
  skuId: number;
  usage: number;
  quantity: number;
  charge: number;
  purchaseText: string;
  quantityOfDataItems: number;
}

// Server-side: BrandedCallingBillingDtos.InvoiceProcessingFailure. The
// "chargedButNotEmailed" flag identifies the rows ops needs to follow up on first —
// those customers were billed but never received the invoice email.
interface InvoiceProcessingFailure {
  invoiceId: number;
  invoiceNum: string | null;
  acctId: string | null;
  total: number;
  chargedButNotEmailed: boolean;
  error: string | null;
}

type ExportTab = "combined" | "fo" | "tns" | "invoice" | "mappings";

const EDIT_FIELD = "inEdit";
const API_BASE = "sysadmin/branded-calling-billing";
const MAX_USAGE_ROWS = 20_000;
const ROW_LIMIT_WARNING = `Showing first ${MAX_USAGE_ROWS.toLocaleString()} rows. Apply a date range filter to narrow results.`;
const PAGE_SIZES = [10, 25, 50, 100, 250];
const PAGE_SIZE_KEY = "bcb-pageSize";

// Excel export column definitions per tab — mirror the visible grid columns (field + title).
// Passed to ExcelExport.save(data, columns) so the spreadsheet headers match the grid.
const EXPORT_COLUMNS: Record<string, ExcelExportColumnProps[]> = {
  combined: [
    { field: "vendor", title: "Vendor" },
    { field: "carrier", title: "Carrier" },
    { field: "acctId", title: "Account ID" },
    { field: "businessName", title: "Business Name" },
    { field: "billingDate", title: "Billing Date" },
    { field: "usage", title: "Usage" },
  ],
  fo: [
    { field: "acctId", title: "Account ID" },
    { field: "business", title: "Business" },
    { field: "businessUnit", title: "Business Unit" },
    { field: "program", title: "Program" },
    { field: "billingMonth", title: "Billing Month" },
    { field: "totalTreatedCalls", title: "Total Treated" },
    { field: "logoCalls", title: "Logo Calls" },
    { field: "tmoTreatedCalls", title: "T-Mobile" },
    { field: "attTreatedCalls", title: "AT&T" },
    { field: "vzwTreatedCalls", title: "Verizon" },
    { field: "boostTreatedCalls", title: "Boost" },
    { field: "rogersBellTreatedCalls", title: "Rogers/Bell" },
    { field: "uniqueANumbers", title: "Unique A#" },
    { field: "answerRate", title: "Answer Rate" },
    { field: "declineRate", title: "Decline Rate" },
    { field: "talkRate10s", title: "Talk Rate 10s" },
    { field: "engagementRate60s", title: "Engage Rate 60s" },
  ],
  tns: [
    { field: "acctId", title: "Account ID" },
    { field: "enterpriseName", title: "Enterprise Name" },
    { field: "terminatingCarrier", title: "Terminating Carrier" },
    { field: "brandingType", title: "Branding Type" },
    { field: "billingDate", title: "Billing Date" },
    { field: "volume", title: "Volume" },
  ],
  invoice: [
    { field: "accountNumId", title: "Account Num ID" },
    { field: "acctId", title: "Account ID" },
    { field: "skuId", title: "SKU ID" },
    { field: "usage", title: "Usage" },
    { field: "quantity", title: "Quantity" },
    { field: "charge", title: "Charge" },
    { field: "purchaseText", title: "Purchase Text" },
    { field: "quantityOfDataItems", title: "Qty of Data Items" },
  ],
  mappings: [
    { field: "vendorName", title: "Vendor" },
    { field: "businessName", title: "Business Name" },
    { field: "acctId", title: "Account ID" },
  ],
};

const EXPORT_FILENAMES: Record<string, string> = {
  combined: "combined-usage.xlsx",
  fo: "first-orion-usage.xlsx",
  tns: "tns-usage.xlsx",
  invoice: "invoice-data.xlsx",
  mappings: "account-id-mappings.xlsx",
};

/**
 * Renders nothing in place of the DatePicker's calendar toggle button, so the Sent on
 * input matches the start/end range inputs — which are bare DateInputs opened by click.
 */
const NoToggleButton = () => null;

/** Last day of the month before today — the default "Sent on" date in the Generate Invoices dialog. */
const lastDayOfPreviousMonth = (): Date => {
  const today = new Date();
  // Day 0 of the current month is the last day of the previous month.
  return new Date(today.getFullYear(), today.getMonth(), 0);
};

/**
 * Branded Calling Billing page.
 * Route: /sysadmin/branded-calling-billing
 */
const BrandedCallingBilling: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  // Single hidden ExcelExport instance; the export button feeds it the active grid's
  // filtered data + columns via save(data, columns).
  const excelExporter = useRef<ExcelExport>(null);

  // AcctIdMap grid state
  const [maps, setMaps] = useState<AcctIdMap[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [editId, setEditId] = useState<number | null>(null);
  const [originalItem, setOriginalItem] = useState<AcctIdMap | null>(null);
  const [activeTab, setActiveTab] = useState<ExportTab>("combined");
  const savedPageSize = Number(localStorage.getItem(PAGE_SIZE_KEY)) || 25;
  const [pageSize, setPageSize] = useState(savedPageSize);
  const [page, setPage] = useState({ skip: 0, take: savedPageSize });
  const [sort, setSort] = useState<SortDescriptor[]>([]);
  const [gridFilter, setGridFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);

  // FO usage state
  const [foData, setFoData] = useState<FoUsageRow[]>([]);
  const [foPage, setFoPage] = useState({ skip: 0, take: savedPageSize });
  const [foSort, setFoSort] = useState<SortDescriptor[]>([]);
  const [foFilter, setFoFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);
  const [foLoaded, setFoLoaded] = useState(false);

  // TNS usage state
  const [tnsData, setTnsData] = useState<TnsUsageRow[]>([]);
  const [tnsPage, setTnsPage] = useState({ skip: 0, take: savedPageSize });
  const [tnsSort, setTnsSort] = useState<SortDescriptor[]>([]);
  const [tnsFilter, setTnsFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);
  const [tnsLoaded, setTnsLoaded] = useState(false);

  // Combined usage state
  const [combinedData, setCombinedData] = useState<CombinedUsageRow[]>([]);
  const [combinedPage, setCombinedPage] = useState({ skip: 0, take: savedPageSize });
  const [combinedSort, setCombinedSort] = useState<SortDescriptor[]>([]);
  const [combinedFilter, setCombinedFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);
  const [combinedLoaded, setCombinedLoaded] = useState(false);

  // Invoice data state
  const [invoiceData, setInvoiceData] = useState<InvoiceDataRow[]>([]);
  const [invoicePage, setInvoicePage] = useState({ skip: 0, take: savedPageSize });
  const [invoiceSort, setInvoiceSort] = useState<SortDescriptor[]>([]);
  const [invoiceFilter, setInvoiceFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);
  const [invoiceLoaded, setInvoiceLoaded] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [invoiceErrors, setInvoiceErrors] = useState<string[]>([]);
  // Pricing-model failure: a message plus the formula's Context (input values) to
  // render as a table so the user can see what caused the calculation to fail.
  const [invoicePricingError, setInvoicePricingError] = useState<{
    message: string;
    formulaName?: string;
    context: Record<string, unknown>;
  } | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  // Date the generated invoices are marked as sent on. Required; defaults to the last
  // day of the previous month, which is the normal billing close date.
  const [sentOn, setSentOn] = useState<Date | null>(lastDayOfPreviousMonth());
  // Removing the toggle button removes Kendo's own way of opening the popup, so the
  // Sent on picker drives `show` itself — same pattern as the DateRangePicker above.
  const [sentOnShow, setSentOnShow] = useState(false);
  const sentOnRef = useRef<HTMLSpanElement>(null);
  const [generating, setGenerating] = useState(false);
  // Live status emitted by the streaming generate-invoices endpoint. Updated on every
  // progress event (per-phase and per-invoice) so the UI shows real activity past the
  // App Service 230s idle window.
  const [generateStatus, setGenerateStatus] = useState<string>("");
  const [generateCurrent, setGenerateCurrent] = useState<number | null>(null);
  const [generateTotal, setGenerateTotal] = useState<number | null>(null);
  const [generateFailedCount, setGenerateFailedCount] = useState<number>(0);
  // Failure detail list, surfaced after the run completes. Populated from the "done"
  // event's result.failures so the UI can show a summary and offer a CSV download.
  const [generateFailures, setGenerateFailures] = useState<InvoiceProcessingFailure[]>([]);

  useEffect(() => {
    loadGridData();
  }, []);

  const loadGridData = async () => {
    try {
      const [mapsData, vendorsData] = await Promise.all([
        callApi(`${API_BASE}/acct-id-map`, HttpMethod.GET),
        callApi(`${API_BASE}/vendors`, HttpMethod.GET),
      ]);
      setMaps(mapsData);
      setVendors(vendorsData);
    } catch {
      toast.error("Failed to load account mappings.");
    }
  };

  const loadFoUsage = async () => {
    try {
      const data = await callApi(`${API_BASE}/fo-usage`, HttpMethod.GET);
      setFoData(data.map((row: FoUsageRow, i: number) => ({ ...row, _rowId: i })));
      setFoLoaded(true);
      if (data.length >= MAX_USAGE_ROWS) toast.warning(ROW_LIMIT_WARNING);
    } catch {
      toast.error("Failed to load First Orion usage data.");
    }
  };

  useEffect(() => {
    if (activeTab === "fo" && !foLoaded) loadFoUsage();
  }, [activeTab, foLoaded]);

  const loadTnsUsage = async () => {
    try {
      const data = await callApi(`${API_BASE}/tns-usage`, HttpMethod.GET);
      setTnsData(data.map((row: TnsUsageRow, i: number) => ({ ...row, _rowId: i })));
      setTnsLoaded(true);
      if (data.length >= MAX_USAGE_ROWS) toast.warning(ROW_LIMIT_WARNING);
    } catch {
      toast.error("Failed to load TNS usage data.");
    }
  };

  useEffect(() => {
    if (activeTab === "tns" && !tnsLoaded) loadTnsUsage();
  }, [activeTab, tnsLoaded]);

  const loadCombinedUsage = async () => {
    try {
      const data = await callApi(`${API_BASE}/combined-usage`, HttpMethod.GET);
      setCombinedData(data.map((row: CombinedUsageRow, i: number) => ({ ...row, _rowId: i })));
      setCombinedLoaded(true);
      if (data.length >= MAX_USAGE_ROWS) toast.warning(ROW_LIMIT_WARNING);
    } catch {
      toast.error("Failed to load combined usage data.");
    }
  };

  useEffect(() => {
    if (activeTab === "combined" && !combinedLoaded) loadCombinedUsage();
  }, [activeTab, combinedLoaded]);

  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const loadInvoiceData = async () => {
    if (!dateRange.start || !dateRange.end) return;
    try {
      setInvoiceError(null);
      setInvoiceErrors([]);
      setInvoicePricingError(null);
      setInvoiceLoading(true);
      const start = formatLocalDate(dateRange.start);
      const end = formatLocalDate(dateRange.end);
      const data = await callApi(`${API_BASE}/invoice-data?startDate=${start}&endDate=${end}`, HttpMethod.GET);
      setInvoiceData(data.map((row: InvoiceDataRow, i: number) => ({ ...row, _rowId: i })));
      setInvoiceLoaded(true);
    } catch (err: unknown) {
      const data = (err as any)?.data;
      const errors = data?.errors as string[] | undefined;
      const context = data?.context as Record<string, unknown> | undefined;
      if (errors && errors.length > 0) {
        setInvoiceErrors(errors);
      } else if (context) {
        // PricingModelException: show the message and the formula Context as a table.
        setInvoicePricingError({
          message: data?.message || "Pricing calculation failed.",
          formulaName: data?.formulaName,
          context,
        });
      } else {
        const msg = (err as { message?: string })?.message || "Failed to load invoice data.";
        setInvoiceError(msg);
      }
      setInvoiceLoaded(true);
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Reset loaded flags — active tab reloads immediately, others reload on next visit
  const reloadUsageGrids = () => {
    setFoLoaded(false);
    setTnsLoaded(false);
    setCombinedLoaded(false);
    setInvoiceLoaded(false);
  };

  const generateInvoices = async () => {
    setShowGenerateModal(false);
    if (!dateRange.start || !dateRange.end || !sentOn) return;
    setGenerating(true);
    setGenerateStatus("Starting…");
    setGenerateCurrent(null);
    setGenerateTotal(null);
    setGenerateFailedCount(0);
    setGenerateFailures([]);

    // The streaming endpoint emits an SSE stream of ProgressEvent objects. We track
    // the final outcome (done / error) outside the callbacks so we can show a single
    // alert when the stream closes.
    let finalMessage: string | null = null;
    let finalError: string | null = null;
    let finalFailures: InvoiceProcessingFailure[] = [];

    try {
      await callStreamingApi(
        `${API_BASE}/generate-invoices-stream`,
        HttpMethod.POST,
        {
          startDate: formatLocalDate(dateRange.start),
          endDate: formatLocalDate(dateRange.end),
          numberOfAccounts: new Set(invoiceData.map(r => r.acctId)).size,
          totalCharge: invoiceData.reduce((sum, r) => sum + (r.charge ?? 0), 0),
          sentOn: formatLocalDate(sentOn),
        },
        (ev: any) => {
          switch (ev?.type) {
            case "progress":
              if (ev.message) setGenerateStatus(ev.message);
              setGenerateCurrent(ev.current ?? null);
              setGenerateTotal(ev.total ?? null);
              break;
            case "invoice-failed":
              // Same progress markers as success — just bump the failure tally.
              if (ev.message) setGenerateStatus(ev.message);
              setGenerateCurrent(ev.current ?? null);
              setGenerateTotal(ev.total ?? null);
              setGenerateFailedCount((n) => n + 1);
              break;
            case "heartbeat":
              // No UI change — the heartbeat exists purely to keep proxies/App Service
              // from idling the connection out while charges and emails are running.
              break;
            case "done":
              finalMessage = ev.result?.message ?? "Invoices generated.";
              finalFailures = ev.result?.failures ?? [];
              break;
            case "error":
              finalError = ev.message ?? "Unknown error";
              break;
          }
        },
      );
    } catch (err: unknown) {
      finalError = (err as { message?: string })?.message || "Unknown error";
    } finally {
      setGenerating(false);
      setGenerateStatus("");
      setGenerateCurrent(null);
      setGenerateTotal(null);
      setGenerateFailures(finalFailures);
    }

    if (finalError) {
      alert(`Failed to generate invoices: ${finalError}`);
    } else if (finalMessage) {
      // Don't block on an alert when we have failures to show — the inline summary
      // below the grid is more useful than a modal.
      if (finalFailures.length === 0) alert(finalMessage);
    }
  };

  /**
   * Build a CSV of the failure list and trigger a download. Columns chosen to give
   * ops everything they need to follow up: which invoice, which customer, how much,
   * whether the customer was charged, and the underlying error message.
   */
  const downloadFailureReport = () => {
    if (generateFailures.length === 0) return;

    const escape = (v: unknown) => {
      const s = v == null ? "" : String(v);
      // Quote anything that contains a comma, quote, or newline; double internal quotes.
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const header = ["invoiceId", "invoiceNum", "acctId", "total", "chargedButNotEmailed", "error"];
    const rows = generateFailures.map((f) => [
      f.invoiceId,
      f.invoiceNum ?? "",
      f.acctId ?? "",
      f.total,
      f.chargedButNotEmailed ? "TRUE" : "FALSE",
      f.error ?? "",
    ].map(escape).join(","));

    const csv = [header.join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.href = url;
    a.download = `branded-calling-invoice-failures-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const response = await callApi(
        `${API_BASE}/import-usage-csv`,
        HttpMethod.POST,
        undefined,
        true,
        undefined,
        file,
      );

      if (response?.type === "success") {
        toast.success(response.message);
        reloadUsageGrids();
      } else {
        toast.error(response?.message ?? "Upload failed.");
      }
    } catch {
      toast.error("Failed to upload CSV.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ── Grid editing ──────────────────────────────────────────────────────

  const addNew = () => {
    if (editId !== null) return;
    if (vendors.length === 0) {
      toast.error("Cannot add mapping until vendors are loaded.");
      return;
    }

    const defaultVendor = vendors[0];
    const newItem: AcctIdMap = {
      acctMapId: 0,
      vendorId: defaultVendor.vendorId,
      vendorName: defaultVendor.vendorName,
      businessName: "",
      acctId: "",
      inEdit: true,
      isNew: true,
    };
    setMaps([newItem, ...maps]);
    setEditId(0);
  };

  const enterEdit = (dataItem: AcctIdMap) => {
    if (editId !== null) return;
    setOriginalItem({ ...dataItem });
    setEditId(dataItem.acctMapId);
  };

  const cancelEdit = () => {
    if (originalItem) {
      setMaps((prev) =>
        prev.map((item) =>
          item.acctMapId === originalItem.acctMapId ? { ...originalItem } : item,
        ),
      );
    } else {
      setMaps((prev) => prev.filter((item) => !item.isNew));
    }
    setEditId(null);
    setOriginalItem(null);
  };

  const saveEdit = async (dataItem: AcctIdMap) => {
    if (!dataItem.businessName.trim() || !dataItem.acctId.trim()) {
      toast.error("Business name and account ID are required.");
      return;
    }

    try {
      if (dataItem.isNew) {
        await callApi(`${API_BASE}/acct-id-map`, HttpMethod.POST, {
          vendorId: dataItem.vendorId,
          businessName: dataItem.businessName,
          acctId: dataItem.acctId,
        });
        toast.success("Mapping created.");
      } else {
        await callApi(`${API_BASE}/acct-id-map`, HttpMethod.PUT, {
          acctMapId: dataItem.acctMapId,
          vendorId: dataItem.vendorId,
          businessName: dataItem.businessName,
          acctId: dataItem.acctId,
        });
        toast.success("Mapping updated.");
      }
      setEditId(null);
      setOriginalItem(null);
      await loadGridData();
      reloadUsageGrids();
    } catch {
      toast.error("Failed to save mapping.");
    }
  };

  const deleteMap = async (acctMapId: number) => {
    if (!confirm("Delete this mapping?")) return;
    try {
      await callApi(`${API_BASE}/acct-id-map/${acctMapId}`, HttpMethod.DELETE);
      toast.success("Mapping deleted.");
      await loadGridData();
      reloadUsageGrids();
    } catch {
      toast.error("Failed to delete mapping.");
    }
  };

  const handleItemChange = (event: GridItemChangeEvent) => {
    const { dataItem, field, value } = event;
    if (!field) return;
    setMaps((prev) =>
      prev.map((item) =>
        item.acctMapId === dataItem.acctMapId ? { ...item, [field]: value } : item,
      ),
    );
  };

  const dataWithEditFlag = useMemo(() => {
    return maps.map((item) => ({
      ...item,
      [EDIT_FIELD]: item.acctMapId === editId || item.isNew,
    }));
  }, [maps, editId]);

  const filteredData = useMemo(
    () => gridFilter ? filterBy(orderBy(dataWithEditFlag, sort), gridFilter) : orderBy(dataWithEditFlag, sort),
    [dataWithEditFlag, sort, gridFilter],
  );

  const pagedData = useMemo(
    () => filteredData.slice(page.skip, page.skip + page.take),
    [filteredData, page],
  );

  const syncPageSize = (newTake: number) => {
    if (newTake !== pageSize) {
      setPageSize(newTake);
      localStorage.setItem(PAGE_SIZE_KEY, String(newTake));
      setPage(() => ({ skip: 0, take: newTake }));
      setFoPage(() => ({ skip: 0, take: newTake }));
      setTnsPage(() => ({ skip: 0, take: newTake }));
      setCombinedPage(() => ({ skip: 0, take: newTake }));
      setInvoicePage(() => ({ skip: 0, take: newTake }));
    }
  };

  const handlePageChange = (e: GridPageChangeEvent) => {
    syncPageSize(e.page.take);
    setPage({ skip: e.page.skip, take: e.page.take });
  };

  const handleSortChange = (e: GridSortChangeEvent) => {
    setSort(e.sort);
    setPage((p) => ({ ...p, skip: 0 }));
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setGridFilter(e.filter);
    setPage((p) => ({ ...p, skip: 0 }));
  };

  // Shared date range filter for all usage grids
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>(() => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
      end: new Date(now.getFullYear(), now.getMonth(), 0),
    };
  });

  // FO usage grid helpers
  const foWithDates = useMemo(
    () => foData.map((row) => ({ ...row, billingMonth: new Date(row.billingMonth) })),
    [foData],
  );

  const foDateFiltered = useMemo(() => {
    let data = foWithDates;
    if (dateRange.start) {
      data = data.filter((r) => r.billingMonth >= dateRange.start!);
    }
    if (dateRange.end) {
      const to = new Date(dateRange.end);
      to.setHours(23, 59, 59, 999);
      data = data.filter((r) => r.billingMonth <= to);
    }
    return data;
  }, [foWithDates, dateRange]);

  const foFiltered = useMemo(
    () => foFilter ? filterBy(orderBy(foDateFiltered, foSort), foFilter) : orderBy(foDateFiltered, foSort),
    [foDateFiltered, foSort, foFilter],
  );

  const foPaged = useMemo(
    () => foFiltered.slice(foPage.skip, foPage.skip + foPage.take),
    [foFiltered, foPage],
  );

  // TNS usage grid helpers
  const tnsWithDates = useMemo(
    () => tnsData.map((row) => ({ ...row, billingDate: new Date(row.billingDate) })),
    [tnsData],
  );

  const tnsDateFiltered = useMemo(() => {
    let data = tnsWithDates;
    if (dateRange.start) {
      data = data.filter((r) => r.billingDate >= dateRange.start!);
    }
    if (dateRange.end) {
      const to = new Date(dateRange.end);
      to.setHours(23, 59, 59, 999);
      data = data.filter((r) => r.billingDate <= to);
    }
    return data;
  }, [tnsWithDates, dateRange]);

  const tnsFiltered = useMemo(
    () => tnsFilter ? filterBy(orderBy(tnsDateFiltered, tnsSort), tnsFilter) : orderBy(tnsDateFiltered, tnsSort),
    [tnsDateFiltered, tnsSort, tnsFilter],
  );

  const tnsPaged = useMemo(
    () => tnsFiltered.slice(tnsPage.skip, tnsPage.skip + tnsPage.take),
    [tnsFiltered, tnsPage],
  );

  // ── Custom cells ──────────────────────────────────────────────────────

  const VendorFilterCell = useCallback(
    (props: any) => {
      const options = ["All", ...vendors.map((v) => v.vendorName)];
      const value = props.value || "All";
      return (
        <div className="k-filtercell">
          <DropDownList
            data={options}
            value={value}
            onChange={(e) => {
              const val = e.value === "All" ? "" : e.value;
              props.onChange({
                value: val,
                operator: val ? "eq" : "",
                syntheticEvent: e.syntheticEvent,
              });
            }}
          />
        </div>
      );
    },
    [vendors],
  );

  const CombinedVendorFilterCell = useCallback((props: any) => {
    const options = ["All", "First Orion", "TNS"];
    const value = props.value || "All";
    return (
      <div className="k-filtercell">
        <DropDownList
          data={options}
          value={value}
          onChange={(e) => {
            const val = e.value === "All" ? "" : e.value;
            props.onChange({
              value: val,
              operator: val ? "eq" : "",
              syntheticEvent: e.syntheticEvent,
            });
          }}
        />
      </div>
    );
  }, []);

  const CombinedCarrierFilterCell = useCallback((props: any) => {
    const options = ["All", "T-Mobile", "Verizon"];
    const value = props.value || "All";
    return (
      <div className="k-filtercell">
        <DropDownList
          data={options}
          value={value}
          onChange={(e) => {
            const val = e.value === "All" ? "" : e.value;
            props.onChange({
              value: val,
              operator: val ? "eq" : "",
              syntheticEvent: e.syntheticEvent,
            });
          }}
        />
      </div>
    );
  }, []);

  const VendorCell = useCallback(
    (props: GridCellProps) => {
      const { dataItem } = props;
      if (dataItem[EDIT_FIELD]) {
        const selected = vendors.find((v) => v.vendorId === dataItem.vendorId);
        return (
          <td>
            <DropDownList
              data={vendors}
              textField="vendorName"
              dataItemKey="vendorId"
              value={selected || null}
              onChange={(e: DropDownListChangeEvent) => {
                const vendor = e.value as Vendor;
                handleItemChange({
                  dataItem,
                  field: "vendorId",
                  value: vendor.vendorId,
                  syntheticEvent: e.syntheticEvent,
                } as unknown as GridItemChangeEvent);
                handleItemChange({
                  dataItem: { ...dataItem, vendorId: vendor.vendorId },
                  field: "vendorName",
                  value: vendor.vendorName,
                  syntheticEvent: e.syntheticEvent,
                } as unknown as GridItemChangeEvent);
              }}
            />
          </td>
        );
      }
      return <td>{dataItem.vendorName}</td>;
    },
    [vendors],
  );

  const CommandCell = useCallback(
    (props: GridCellProps) => {
      const { dataItem } = props;
      const isInEdit = dataItem[EDIT_FIELD];
      return (
        <td>
          {isInEdit ? (
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-primary" onClick={() => saveEdit(dataItem)}>
                {dataItem.isNew ? "Add" : "Update"}
              </button>
              <button className="btn btn-sm btn-secondary" onClick={cancelEdit}>
                Cancel
              </button>
            </div>
          ) : (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => enterEdit(dataItem)}
                disabled={editId !== null}
              >
                Edit
              </button>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => deleteMap(dataItem.acctMapId)}
                disabled={editId !== null}
              >
                Delete
              </button>
            </div>
          )}
        </td>
      );
    },
    [editId],
  );

  const BillingMonthCell = useCallback((props: GridCellProps) => {
    const value = props.dataItem[props.field || ""];
    if (!value) return <td />;
    const date = value instanceof Date ? value : new Date(value);
    return <td>{date.toLocaleDateString()}</td>;
  }, []);

  const RightAlignedNumberCell = useCallback((props: GridCellProps) => {
    const value = props.dataItem[props.field || ""];
    return <td style={{ textAlign: "right" }}>{value != null ? Number(value).toLocaleString() : ""}</td>;
  }, []);

  const DollarCell = useCallback((props: GridCellProps) => {
    const value = props.dataItem[props.field || ""];
    return <td style={{ textAlign: "right" }}>{value != null ? `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}</td>;
  }, []);

  // Combined usage grid helpers
  const combinedWithDates = useMemo(
    () => combinedData.map((row) => ({ ...row, billingDate: new Date(row.billingDate) })),
    [combinedData],
  );

  const combinedDateFiltered = useMemo(() => {
    let data = combinedWithDates;
    if (dateRange.start) {
      data = data.filter((r) => r.billingDate >= dateRange.start!);
    }
    if (dateRange.end) {
      const to = new Date(dateRange.end);
      to.setHours(23, 59, 59, 999);
      data = data.filter((r) => r.billingDate <= to);
    }
    return data;
  }, [combinedWithDates, dateRange]);

  const combinedFiltered = useMemo(
    () => combinedFilter ? filterBy(orderBy(combinedDateFiltered, combinedSort), combinedFilter) : orderBy(combinedDateFiltered, combinedSort),
    [combinedDateFiltered, combinedSort, combinedFilter],
  );

  const combinedPaged = useMemo(
    () => combinedFiltered.slice(combinedPage.skip, combinedPage.skip + combinedPage.take),
    [combinedFiltered, combinedPage],
  );

  // Invoice data pipeline (no date filtering needed — server filters by date params)
  const invoiceFiltered = useMemo(
    () => invoiceFilter ? filterBy(orderBy(invoiceData, invoiceSort), invoiceFilter) : orderBy(invoiceData, invoiceSort),
    [invoiceData, invoiceSort, invoiceFilter],
  );

  const invoicePaged = useMemo(
    () => invoiceFiltered.slice(invoicePage.skip, invoicePage.skip + invoicePage.take),
    [invoiceFiltered, invoicePage],
  );

  // Reload invoice data when date range changes (even if not on invoice tab — will load on next visit)
  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      setInvoiceLoaded(false);
    }
  }, [dateRange]);

  useEffect(() => {
    if (activeTab === "invoice" && !invoiceLoaded && dateRange.start && dateRange.end) loadInvoiceData();
  }, [activeTab, invoiceLoaded, dateRange]);

  // Shared date range change handler — resets all grid pages
  const [datePickerShow, setDatePickerShow] = useState(false);

  const resetAllGridPages = useCallback(() => {
    setFoPage((p) => ({ ...p, skip: 0 }));
    setTnsPage((p) => ({ ...p, skip: 0 }));
    setCombinedPage((p) => ({ ...p, skip: 0 }));
    setInvoicePage((p) => ({ ...p, skip: 0 }));
  }, []);

  const handleDateRangeChange = useCallback((e: DateRangePickerChangeEvent) => {
    setDateRange({ start: e.value.start, end: e.value.end });
    if (e.value.start && e.value.end) setDatePickerShow(false);
    resetAllGridPages();
  }, [resetAllGridPages]);

  const setLastMonth = useCallback(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    setDateRange({ start, end });
    resetAllGridPages();
  }, [resetAllGridPages]);

  const clearDateRange = useCallback(() => {
    setDateRange({ start: null, end: null });
    resetAllGridPages();
  }, [resetAllGridPages]);

  const DateRangePopup = useCallback(
    (props: any) => (
      <Popup {...props}>
        <div style={{ borderBottom: "1px solid #dee2e6", padding: "6px 12px", textAlign: "center" }}>
          <a href="#" style={{ fontSize: "0.85em" }} onClick={(e) => { e.preventDefault(); setLastMonth(); setDatePickerShow(false); }}>Last Month</a>
        </div>
        {props.children}
      </Popup>
    ),
    [setLastMonth],
  );

  // Export the currently active grid to Excel, honoring its applied filters/sort.
  // Each ...Filtered memo already reflects what the grid shows (full filtered set, not
  // just the current page), so we hand that exact array to ExcelExport.
  const exportToExcel = () => {
    if (activeTab === "invoice") {
      if (!dateRange.start || !dateRange.end) {
        toast.info("Pick a date range before exporting invoice data.");
        return;
      }
      if (invoiceLoading) {
        toast.info("Invoice data is still loading.");
        return;
      }
      if (invoiceError || invoiceErrors.length > 0 || invoicePricingError) {
        toast.info("Invoice data has errors — fix them before exporting.");
        return;
      }
    }

    const exportByTab: Record<ExportTab, unknown[]> = {
      combined: combinedFiltered,
      fo: foFiltered,
      tns: tnsFiltered,
      invoice: invoiceFiltered,
      mappings: filteredData,
    };

    const data = exportByTab[activeTab];
    const columns = EXPORT_COLUMNS[activeTab];

    if (data.length === 0) {
      toast.info("Nothing to export — the current grid has no rows.");
      return;
    }

    excelExporter.current?.save(data, columns);
  };

  return (
    <div className={styles.container}>
      <div className={styles["tab-bar"]}>
        <div className="btn-group btn-group-sm">
          {([
            { key: "combined" as const, label: "Combined Usage" },
            { key: "fo" as const, label: "First Orion Usage" },
            { key: "tns" as const, label: "TNS Usage" },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              className={`btn ${activeTab === tab.key ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <button
          className={`btn btn-sm ${activeTab === "invoice" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setActiveTab("invoice")}
          disabled={invoiceLoading}
        >
          {invoiceLoading ? "Loading Invoice Data…" : "Invoice Data"}
        </button>

        <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            show={datePickerShow}
            onOpen={() => setDatePickerShow(true)}
            onClose={() => setDatePickerShow(false)}
            format="MM/dd/yyyy"
            startDateInputSettings={{ label: "" }}
            endDateInputSettings={{ label: "" }}
            popup={DateRangePopup}
          />
          <button
            className="k-button k-button-md k-button-solid k-button-solid-base k-rounded-md k-icon-button"
            title="Clear date filter"
            disabled={!dateRange.start && !dateRange.end}
            onClick={clearDateRange}
          >
            <span className="k-icon k-svg-icon k-svg-i-filter-clear k-button-icon" aria-hidden="true">
              <svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path d="m143.5 64 168.2 168.2L288 256v160l-64 64V256L64 96V64zm236.1 100.4L448 96V64H279.3l-64-64L192 22l298 298 22-23.3z" />
              </svg>
            </span>
          </button>
        </div>

        <button
          className={`btn btn-sm ${activeTab === "mappings" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setActiveTab("mappings")}
        >
          Account ID Mappings
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? "Uploading…" : "Import Usage CSV"}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={exportToExcel}
            title="Export the current grid (with filters applied) to Excel"
          >
            <i className="bi bi-file-earmark-excel me-1" />
            Export to Excel
          </button>
        </div>
      </div>

      {/* Hidden, reused for every tab — exportToExcel feeds it the active grid's data + columns. */}
      <ExcelExport ref={excelExporter} fileName={EXPORT_FILENAMES[activeTab]} />

      <div className="mt-3">
        {activeTab === "combined" && (
          <Grid
            className="sysadmin-grid"
            data={combinedPaged}
            total={combinedFiltered.length}
            skip={combinedPage.skip}
            take={combinedPage.take}
            dataItemKey="_rowId"
            sortable={true}
            resizable={true}
            sort={combinedSort}
            onSortChange={(e) => { setCombinedSort(e.sort); setCombinedPage((p) => ({ ...p, skip: 0 })); }}
            filterable={true}
            filter={combinedFilter}
            onFilterChange={(e) => { setCombinedFilter(e.filter); setCombinedPage((p) => ({ ...p, skip: 0 })); }}
            pageable={{ pageSizes: PAGE_SIZES }}
            onPageChange={(e) => { syncPageSize(e.page.take); setCombinedPage({ skip: e.page.skip, take: e.page.take }); }}
            style={{ height: "calc(100vh - 135px)", width: "100%" }}
          >
            <Column field="vendor" title="Vendor" width={150} filterCell={CombinedVendorFilterCell} />
            <Column field="carrier" title="Carrier" width={150} filterCell={CombinedCarrierFilterCell} />
            <Column field="acctId" title="Account ID" width={150} filter="text" />
            <Column field="businessName" title="Business Name" width={350} filter="text" />
            <Column field="billingDate" title="Billing Date" width={150} cells={{ data: BillingMonthCell }} filterable={false} />
            <Column field="usage" title="Usage" width={140} filterable={false} />
          </Grid>
        )}

        {activeTab === "fo" && (
          <Grid
            className="sysadmin-grid"
            data={foPaged}
            total={foFiltered.length}
            skip={foPage.skip}
            take={foPage.take}
            dataItemKey="_rowId"
            sortable={true}
            resizable={true}
            sort={foSort}
            onSortChange={(e) => { setFoSort(e.sort); setFoPage((p) => ({ ...p, skip: 0 })); }}
            filterable={true}
            filter={foFilter}
            onFilterChange={(e) => { setFoFilter(e.filter); setFoPage((p) => ({ ...p, skip: 0 })); }}
            pageable={{ pageSizes: PAGE_SIZES }}
            onPageChange={(e) => { syncPageSize(e.page.take); setFoPage({ skip: e.page.skip, take: e.page.take }); }}
            style={{ height: "calc(100vh - 135px)", width: "100%" }}
          >
            <Column field="acctId" title="Account ID" width={150} filter="text" />
            <Column field="business" title="Business" width={350} filter="text" />
            <Column field="businessUnit" title="Business Unit" width={350} filter="text" />
            <Column field="program" title="Program" width={350} filter="text" />
            <Column field="billingMonth" title="Billing Month" width={150} cells={{ data: BillingMonthCell }} filterable={false} />
            <Column field="totalTreatedCalls" title="Total Treated" width={160} filterable={false} />
            <Column field="logoCalls" title="Logo Calls" width={140} filterable={false} />
            <Column field="tmoTreatedCalls" title="T-Mobile" width={140} filterable={false} />
            <Column field="attTreatedCalls" title="AT&T" width={130} filterable={false} />
            <Column field="vzwTreatedCalls" title="Verizon" width={140} filterable={false} />
            <Column field="boostTreatedCalls" title="Boost" width={130} filterable={false} />
            <Column field="rogersBellTreatedCalls" title="Rogers/Bell" width={150} filterable={false} />
            <Column field="uniqueANumbers" title="Unique A#" width={140} filterable={false} />
            <Column field="answerRate" title="Answer Rate" width={180} filterable={false} />
            <Column field="declineRate" title="Decline Rate" width={180} filterable={false} />
            <Column field="talkRate10s" title="Talk Rate 10s" width={180} filterable={false} />
            <Column field="engagementRate60s" title="Engage Rate 60s" width={180} filterable={false} />
          </Grid>
        )}

        {activeTab === "tns" && (
          <Grid
            className="sysadmin-grid"
            data={tnsPaged}
            total={tnsFiltered.length}
            skip={tnsPage.skip}
            take={tnsPage.take}
            dataItemKey="_rowId"
            sortable={true}
            resizable={true}
            sort={tnsSort}
            onSortChange={(e) => { setTnsSort(e.sort); setTnsPage((p) => ({ ...p, skip: 0 })); }}
            filterable={true}
            filter={tnsFilter}
            onFilterChange={(e) => { setTnsFilter(e.filter); setTnsPage((p) => ({ ...p, skip: 0 })); }}
            pageable={{ pageSizes: PAGE_SIZES }}
            onPageChange={(e) => { syncPageSize(e.page.take); setTnsPage({ skip: e.page.skip, take: e.page.take }); }}
            style={{ height: "calc(100vh - 135px)", width: "100%" }}
          >
            <Column field="acctId" title="Account ID" width={150} filter="text" />
            <Column field="enterpriseName" title="Enterprise Name" width={450} filter="text" />
            <Column field="terminatingCarrier" title="Terminating Carrier" width={200} filter="text" />
            <Column field="brandingType" title="Branding Type" width={180} filter="text" />
            <Column field="billingDate" title="Billing Date" width={150} cells={{ data: BillingMonthCell }} filterable={false} />
            <Column field="volume" title="Volume" width={140} filterable={false} />
          </Grid>
        )}

        {activeTab === "invoice" && (
          !dateRange.start || !dateRange.end ? (
            <div className="alert alert-info mt-2" role="alert">
              Select a date range to load invoice data.
            </div>
          ) : invoiceLoading ? (
            <Loading />
          ) : generating ? (
            <div className="mt-2">
              <div className="alert alert-info" role="alert">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  <strong>{generateStatus || "Generating invoices…"}</strong>
                </div>
                {generateCurrent != null && generateTotal != null && generateTotal > 0 && (
                  <>
                    <div className="progress" style={{ height: 8 }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${Math.round((generateCurrent / generateTotal) * 100)}%` }}
                        aria-valuenow={generateCurrent}
                        aria-valuemin={0}
                        aria-valuemax={generateTotal}
                      />
                    </div>
                    <small className="text-muted">
                      {generateCurrent.toLocaleString()} of {generateTotal.toLocaleString()}
                      {generateFailedCount > 0 && (
                        <> &middot; <span className="text-danger">{generateFailedCount.toLocaleString()} failed</span></>
                      )}
                    </small>
                  </>
                )}
              </div>
            </div>
          ) : invoiceErrors.length > 0 ? (
            <div className="alert alert-danger mt-2" role="alert">
              <strong>Mapping validation failed:</strong>
              <ul className="mb-0 mt-1">
                {invoiceErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          ) : invoicePricingError ? (
            <div className="alert alert-danger mt-2" role="alert">
              <strong>Pricing calculation failed:</strong> {invoicePricingError.message}
              {invoicePricingError.formulaName && (
                <div className="mt-1">
                  <strong>Formula:</strong> {invoicePricingError.formulaName}
                </div>
              )}
              {invoicePricingError.context.modelId != null &&
                invoicePricingError.context.modelId !== "" && (
                  <div className="mt-1">
                    <Link
                      to={`/sysadmin/product-pricing-models/${invoicePricingError.context.modelId}`}
                    >
                      View pricing model #{String(invoicePricingError.context.modelId)}
                    </Link>
                  </div>
                )}
              {Object.keys(invoicePricingError.context).length > 0 && (
                <table className="table table-sm table-bordered mb-0 mt-2 bg-white">
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(invoicePricingError.context).map(([key, value]) => (
                      <tr key={key}>
                        <td>{key}</td>
                        <td>
                          {value === null || value === undefined
                            ? ""
                            : typeof value === "object"
                              ? JSON.stringify(value)
                              : String(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : invoiceError ? (
            <div className="alert alert-danger mt-2" role="alert">
              {invoiceError}
            </div>
          ) : (
            <div>
              {invoiceData.length > 0 && (
                <div className="mb-2 d-flex align-items-center gap-2">
                  <label className="mb-0" htmlFor="sentOnDate">
                    Sent on
                  </label>
                  <span
                    ref={sentOnRef}
                    onClick={(e) => {
                      // The calendar popup is portaled to <body>, so clicks inside it bubble
                      // here through the React tree without being DOM descendants. Ignoring
                      // those keeps picking a date from reopening the popup Kendo just closed.
                      if (sentOnRef.current?.contains(e.target as Node)) setSentOnShow(true);
                    }}
                  >
                    <DatePicker
                      id="sentOnDate"
                      value={sentOn}
                      onChange={(e: DatePickerChangeEvent) => setSentOn(e.value)}
                      format="MM/dd/yyyy"
                      show={sentOnShow}
                      onOpen={() => setSentOnShow(true)}
                      onClose={() => setSentOnShow(false)}
                      toggleButton={NoToggleButton}
                      // DatePicker renders its wrapper with style={{ width }}, so a `style`
                      // prop is ignored — the width has to go through this prop. 10em matches
                      // .k-daterangepicker .k-dateinput in the Kendo theme.
                      width="10em"
                    />
                  </span>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => setShowGenerateModal(true)}
                    disabled={generating || !sentOn}
                  >
                    {generating ? "Generating Invoices…" : "Generate Invoices"}
                  </button>
                  {!sentOn && <span className="text-danger small">Sent on date is required.</span>}
                </div>
              )}

              {generateFailures.length > 0 && (
                <div className="alert alert-warning d-flex justify-content-between align-items-start" role="alert">
                  <div>
                    <strong>
                      {generateFailures.length.toLocaleString()} invoice{generateFailures.length === 1 ? "" : "s"} need review.
                    </strong>{" "}
                    {generateFailures.some((f) => f.chargedButNotEmailed) && (
                      <span className="badge bg-danger ms-1">
                        {generateFailures.filter((f) => f.chargedButNotEmailed).length.toLocaleString()} charged without email — urgent
                      </span>
                    )}
                    <div className="mt-2 small">
                      Download the CSV for the full punch list (invoice id, acctId, amount, charge state, error).
                    </div>
                  </div>
                  <div className="d-flex gap-2 ms-3 flex-shrink-0">
                    <button className="btn btn-sm btn-outline-secondary" onClick={downloadFailureReport}>
                      <i className="bi bi-download me-1" />
                      Download CSV
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setGenerateFailures([])} title="Dismiss">
                      <i className="bi bi-x-lg" />
                    </button>
                  </div>
                </div>
              )}
              <Grid
                className="sysadmin-grid"
                data={invoicePaged}
                total={invoiceFiltered.length}
                skip={invoicePage.skip}
                take={invoicePage.take}
                dataItemKey="_rowId"
                sortable={true}
                resizable={true}
                sort={invoiceSort}
                onSortChange={(e) => { setInvoiceSort(e.sort); setInvoicePage((p) => ({ ...p, skip: 0 })); }}
                filterable={true}
                filter={invoiceFilter}
                onFilterChange={(e) => { setInvoiceFilter(e.filter); setInvoicePage((p) => ({ ...p, skip: 0 })); }}
                pageable={{ pageSizes: PAGE_SIZES }}
                onPageChange={(e) => { syncPageSize(e.page.take); setInvoicePage({ skip: e.page.skip, take: e.page.take }); }}
                style={{ height: "calc(100vh - 170px)", width: "100%" }}
              >
                <Column field="accountNumId" title="Account Num ID" width={160} filter="text" />
                <Column field="acctId" title="Account ID" width={200} filter="text" />
                <Column field="skuId" title="SKU ID" width={120} filter="text" />
                <Column field="usage" title="Usage" width={120} filterable={false} cells={{ data: RightAlignedNumberCell }} />
                <Column field="quantity" title="Quantity" width={120} filterable={false} cells={{ data: RightAlignedNumberCell }} />
                <Column field="charge" title="Charge" width={120} filterable={false} cells={{ data: DollarCell }} />
                <Column field="purchaseText" title="Purchase Text" width={600} filter="text" />
                <Column field="quantityOfDataItems" title="Qty of Data Items" width={160} filterable={false} cells={{ data: RightAlignedNumberCell }} />
              </Grid>
            </div>
          )
        )}

        {showGenerateModal && (
          <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Generate Invoices</h5>
                  <button type="button" className="btn-close" onClick={() => setShowGenerateModal(false)} />
                </div>
                <div className="modal-body">
                  <p>
                    Generate invoices for{" "}
                    <strong>
                      {dateRange.start?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      {" – "}
                      {dateRange.end?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </strong>
                    ?
                  </p>
                  <ul className="mb-0">
                    <li>Accounts: <strong>{new Set(invoiceData.map(r => r.acctId)).size.toLocaleString()}</strong></li>
                    <li>Total charge: <strong>${invoiceData.reduce((sum, r) => sum + (r.charge ?? 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></li>
                    <li>Sent on: <strong>{sentOn?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</strong></li>
                  </ul>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary btn-sm" onClick={() => setShowGenerateModal(false)}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={generateInvoices}>Generate</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "mappings" && (
          <div>
            <div className="mb-2">
              <button className="btn btn-success btn-sm" onClick={addNew} disabled={editId !== null}>
                + Add Mapping
              </button>
            </div>

            <Grid
              className="sysadmin-grid"
              data={pagedData}
              total={filteredData.length}
              skip={page.skip}
              take={page.take}
              dataItemKey="acctMapId"
              sortable={true}
              resizable={true}
              sort={sort}
              onSortChange={handleSortChange}
              filterable={true}
              filter={gridFilter}
              onFilterChange={handleFilterChange}
              pageable={{ pageSizes: PAGE_SIZES }}
              onPageChange={handlePageChange}
              editField={EDIT_FIELD}
              onItemChange={handleItemChange}
              style={{ height: "calc(100vh - 170px)", width: "100%" }}
            >
              <Column
                field="vendorName"
                title="Vendor"
                width={200}
                cells={{ data: VendorCell }}
                filterCell={VendorFilterCell}
              />
              <Column field="businessName" title="Business Name" width={300} filter="text" />
              <Column field="acctId" title="Account ID" width={200} filter="text" />
              <Column
                title="Actions"
                width={200}
                filterable={false}
                sortable={false}
                cells={{ data: CommandCell }}
              />
            </Grid>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandedCallingBilling;
